import React, { useState, useEffect, useCallback } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Plus, Trash2, Edit2, Save, X, Loader2, Sparkles, Wrench, Bug, Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const TAG_OPTIONS = [
  { value: 'new_feature', label: 'New Feature', Icon: Sparkles, color: '#10B981' },
  { value: 'improvement', label: 'Improvement', Icon: Wrench, color: '#3B82F6' },
  { value: 'bug_fix', label: 'Bug Fix', Icon: Bug, color: '#F59E0B' },
  { value: 'announcement', label: 'Announcement', Icon: Megaphone, color: '#8B5CF6' },
];

const tagMap = Object.fromEntries(TAG_OPTIONS.map(t => [t.value, t]));

function EntryForm({ entry, onSave, onCancel, colors, saving }) {
  const [title, setTitle] = useState(entry?.title || '');
  const [description, setDescription] = useState(entry?.description || '');
  const [tag, setTag] = useState(entry?.tag || 'new_feature');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    onSave({ title: title.trim(), description: description.trim(), tag });
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-xl p-5 space-y-4" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
          style={{ background: colors.inputBg, border: `1px solid ${colors.cardBorder}`, color: colors.text }}
          placeholder="e.g. Expanded Commentary Coverage"
          data-testid="changelog-title-input"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Description</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
          style={{ background: colors.inputBg, border: `1px solid ${colors.cardBorder}`, color: colors.text }}
          placeholder="Describe the update..."
          data-testid="changelog-description-input"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold mb-1.5" style={{ color: colors.textMuted }}>Tag</label>
        <div className="flex flex-wrap gap-2">
          {TAG_OPTIONS.map(t => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTag(t.value)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{
                background: tag === t.value ? `${t.color}20` : 'transparent',
                border: `1px solid ${tag === t.value ? t.color : colors.cardBorder}`,
                color: tag === t.value ? t.color : colors.textMuted,
              }}
              data-testid={`changelog-tag-${t.value}`}
            >
              <t.Icon className="h-3 w-3" />
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} className="text-xs" data-testid="changelog-cancel-btn">
          <X className="h-3 w-3 mr-1" /> Cancel
        </Button>
        <Button type="submit" size="sm" disabled={saving} className="text-xs" style={{ background: colors.accent, color: '#fff' }} data-testid="changelog-save-btn">
          {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
          {entry ? 'Update' : 'Publish'}
        </Button>
      </div>
    </form>
  );
}

export default function AdminChangelogPage() {
  const { colors } = useAdminTheme();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/changelog`, { withCredentials: true });
      setEntries(res.data);
    } catch (err) {
      toast.error('Failed to load changelog entries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editingEntry) {
        await axios.put(`${API}/admin/changelog/${editingEntry.entry_id}`, data, { withCredentials: true });
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/admin/changelog`, data, { withCredentials: true });
        toast.success('Entry published');
      }
      setShowForm(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this entry? Users will no longer see it.')) return;
    try {
      await axios.delete(`${API}/admin/changelog/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const startEdit = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  return (
    <AdminLayout>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }} data-testid="changelog-page-title">What's New</h1>
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Manage update notes shown to users in the app.</p>
          </div>
          {!showForm && (
            <Button
              onClick={() => { setEditingEntry(null); setShowForm(true); }}
              size="sm"
              className="text-xs"
              style={{ background: colors.accent, color: '#fff' }}
              data-testid="changelog-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> New Entry
            </Button>
          )}
        </div>

        {showForm && (
          <div className="mb-6">
            <EntryForm
              entry={editingEntry}
              onSave={handleSave}
              onCancel={cancelForm}
              colors={colors}
              saving={saving}
            />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: colors.accent }} />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 rounded-xl" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }}>
            <Sparkles className="h-10 w-10 mx-auto mb-3" style={{ color: colors.textMuted }} />
            <p className="text-sm font-medium" style={{ color: colors.text }}>No entries yet</p>
            <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Publish your first "What's New" update for users.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const tagInfo = tagMap[entry.tag] || tagMap.new_feature;
              return (
                <div key={entry.entry_id} className="rounded-xl p-4 flex gap-4" style={{ background: colors.card, border: `1px solid ${colors.cardBorder}` }} data-testid={`changelog-entry-${entry.entry_id}`}>
                  <div className="shrink-0 mt-0.5">
                    <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${tagInfo.color}15` }}>
                      <tagInfo.Icon className="h-4 w-4" style={{ color: tagInfo.color }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold truncate" style={{ color: colors.text }}>{entry.title}</h3>
                        <p className="text-xs mt-1 whitespace-pre-wrap break-words" style={{ color: colors.textMuted }}>{entry.description}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(entry)} className="p-1.5 rounded-lg transition hover:bg-black/5" title="Edit" data-testid={`changelog-edit-${entry.entry_id}`}>
                          <Edit2 className="h-3.5 w-3.5" style={{ color: colors.textMuted }} />
                        </button>
                        <button onClick={() => handleDelete(entry.entry_id)} className="p-1.5 rounded-lg transition hover:bg-red-50" title="Delete" data-testid={`changelog-delete-${entry.entry_id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${tagInfo.color}15`, color: tagInfo.color }}>
                        {tagInfo.label}
                      </span>
                      <span className="text-[10px]" style={{ color: colors.textMuted }}>
                        {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                      {entry.created_by && (
                        <span className="text-[10px]" style={{ color: colors.textMuted }}>by {entry.created_by}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
